// src/lib/guideLinks.js
// Which part of the user manual explains the screen somebody is standing on.
// Gate decision D17, Session 78o.
//
// The manual is published at /guide and every section carries a stable anchor
// derived from its number — "3.2" is `#s-3-2`, "A1" is `#s-a1`. Those anchors
// are emitted by scripts/guides/build_section.py, so this map and the document
// cannot drift apart silently: a wrong key here lands the reader at the top of
// the manual rather than on a broken page.
//
// WHY A MAP RATHER THAN A CLEVERER SCHEME. Page keys and section numbers are
// two different vocabularies owned by two different things — the app's router
// and the document's running order — and deriving one from the other would mean
// renumbering the manual could silently repoint Help. An explicit map is a list
// of decisions somebody can read.
//
// WHAT IS DELIBERATELY ABSENT, and why -- checked at Session 78o after Colby
// reported the menu saying "User guide" on an iPad instead of "Help with this
// screen". That is this fallback firing, not a defect: the logic is unchanged
// since the link was written, and it was proven live on seven pages.
//
// Eighteen page keys have no entry. Nine are Tools, which is out of the guide's
// scope by decision. The rest are unmapped because THE MANUAL HAS NO SECTION
// FOR THEM, which is #455 wearing a different hat -- the gate scoped both the
// writing and the screenshots by TASK, so screens a person merely LOOKS at got
// neither:
//
//   requests, requests_inbox  the approval inbox. s-3-8 is the REQUESTER's view
//                             of their own request; pointing a manager there
//                             sends them to somebody else's screen.
//   inv_ledger, inv_labels    no section exists.
//   notifications,            s-1-1 mentions notifications zero times -- counted.
//   notification_preferences
//   import                    master-data import is a future sprint.
//   speak_up_admin            out of scope with Safety and Speak Up.
//
// The fix is to WRITE those sections in 78p and map them then. Do not map them
// to the nearest-looking anchor in the meantime.
//
// Deliberately incomplete. A key with no entry gets the manual's front page,
// which is a correct answer rather than a failure: the contents page is one
// screen away and it names every section with who can use it. Sending somebody
// to a *wrong* section would be worse than sending them to the front.

// Points straight at the published manual. It used to point at /guide.html,
// which was a byte-identical SECOND COPY of the same 3MB document, kept only so
// this one link had somewhere to go -- so every guide rebuild committed the
// manual twice. There is exactly one reference to it (this constant), so nothing
// needed a redirect: the link just moves. guideHref() still appends the #section,
// so Help lands on the right part of the page rather than the front.
const GUIDE_PATH = '/guides/Stede_User_Manual.html';

const SECTION_FOR_PAGE = {
  // Getting started
  profile: 's-1-1',

  // Inventory — the eight task sections
  inv_home: 's-3-1',
  inv_receive: 's-3-2',
  inv_request: 's-3-3',
  inv_request_materials: 's-3-3',
  inv_use: 's-3-5',
  inv_adjust: 's-3-6',
  inv_catalog: 's-3-7',
  inv_detail: 's-3-12',   // its own section since 78p; was the catalog's
  my_requests: 's-3-8',
  // #455, Session 78p. These four had no section until 78p wrote one, which is
  // why Help degraded to "User guide" on them and landed at the top of the
  // manual. They are mapped now because the sections exist -- not before.
  requests_inbox: 's-3-9',
  requests: 's-3-9',
  inv_ledger: 's-3-10',
  inv_labels: 's-3-11',

  // Purchasing
  purchasing_queue: 's-p1',
  purchasing_vendors: 's-p5',   // was s-p2 (Placing the order) until P5 existed
  inv_offcatalog: 's-p4',

  // Invoicing
  purchasing_invoicing: 's-i1',

  // Administration
  // insights -> A7 because A7 is literally "Printers, spending limits and
  // comparing branches" and names Insights twice. Checked by reading A7's body,
  // not assumed from its title: a WRONG anchor is worse than the front page,
  // which is why the other unmapped screens below stay unmapped.
  insights: 's-a8',   // was s-a7, which only MENTIONED Insights; A8 is about it
  users: 's-a1',
  company_settings: 's-a2',
  integrations: 's-a6',
  printer_registry: 's-a7',
};

// The manual's own front page, for anywhere not mapped.
export function guideHref(pageKey) {
  const anchor = SECTION_FOR_PAGE[pageKey];
  return anchor ? `${GUIDE_PATH}#${anchor}` : GUIDE_PATH;
}

// Whether Help can offer something more specific than the front page. Used to
// word the menu item honestly — "help with this screen" is a promise, and it
// should only be made when it can be kept.
export function hasGuideSection(pageKey) {
  return Boolean(SECTION_FOR_PAGE[pageKey]);
}

export const GUIDE_URL = GUIDE_PATH;
